import React, { createContext, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Provides `previousRoute` - url pathname of the route user was on previously. If null, url has just changed and previousRoute is currently being set. If undefined, there is no previousRoute.
 */
export const PreviousRouteContext = createContext<{
  previousRoute: string | null | undefined
}>({
  previousRoute: null,
})

/** Wrap this around Routes in order to track which route the user was previously on. `previousRoute` is a url pathname and can be accessed from `PreviousRouteContext`  */
export const PreviousRouteProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  // Track the route transition synchronously during render rather than in an effect. An effect-based
  // update only resolves after the first render commits, and React flushes effects child-first, so a
  // child that reads `previousRoute` in its own mount effect would see a value that is stale by one
  // navigation (e.g. RedesignedClient capturing the wrong back-navigation entry point). Updating refs
  // during render with a guard keeps `previousRoute` correct on the very first render of each route.
  const previousRouteRef = useRef<string | undefined>(undefined)
  const currentRouteRef = useRef<string | undefined>(undefined)

  if (currentRouteRef.current !== location.pathname) {
    previousRouteRef.current = currentRouteRef.current
    currentRouteRef.current = location.pathname
  }
  const previousRoute = previousRouteRef.current

  return (
    <PreviousRouteContext.Provider
      value={{
        previousRoute,
      }}
    >
      {children}
    </PreviousRouteContext.Provider>
  )
}
